import { WorkPolicyFormErrors, WorkPolicyFormValue } from "./hooks/use-work-policy-form";
import {
  calculateAutomaticBreakMinutes,
  calculateExpectedEndTime
} from "./work-policy.validation";
import {
  ActionButton,
  ActionRow,
  ErrorText,
  Field,
  FormGrid,
  FormSection,
  RuleList
} from "./work-type-management.styles";

interface StaggeredRuleSectionProps {
  value: WorkPolicyFormValue;
  errors: WorkPolicyFormErrors;
  onChange: <K extends keyof WorkPolicyFormValue>(
    field: K,
    value: WorkPolicyFormValue[K]
  ) => void;
  readOnly?: boolean;
}

export const StaggeredRuleSection = ({
  value,
  errors,
  onChange,
  readOnly = false
}: StaggeredRuleSectionProps) => {
  if (value.policyKind === "APPROVAL") {
    const updateTime = (index: number, next: string) =>
      onChange(
        "staggeredStartTimes",
        value.staggeredStartTimes.map((time, current) => (current === index ? next : time))
      );
    const move = (index: number, offset: number) => {
      const target = index + offset;
      if (target < 0 || target >= value.staggeredStartTimes.length) return;
      const next = [...value.staggeredStartTimes];
      [next[index], next[target]] = [next[target], next[index]];
      onChange("staggeredStartTimes", next);
    };

    return (
      <FormSection>
        <legend>시차출퇴근제 추가 설정</legend>
        <p>신청자가 선택할 수 있는 출근 시간을 위에서 아래 순서로 저장합니다.</p>
        <RuleList>
          {value.staggeredStartTimes.map((time, index) => (
            <li key={`${index}-${time}`}>
              <Field>
                허용 출근 시간 {index + 1}
                <input
                  id={index === 0 ? "work-policy-staggeredStartTimes" : undefined}
                  type="time"
                  value={time}
                  disabled={readOnly}
                  aria-invalid={Boolean(errors.staggeredStartTimes)}
                  aria-describedby={errors.staggeredStartTimes ? "work-policy-staggeredStartTimes-error" : undefined}
                  onChange={event => updateTime(index, event.currentTarget.value)}
                />
              </Field>
              {!readOnly && <ActionRow>
                <ActionButton type="button" disabled={index === 0} onClick={() => move(index, -1)}>
                  위로
                </ActionButton>
                <ActionButton
                  type="button"
                  disabled={index === value.staggeredStartTimes.length - 1}
                  onClick={() => move(index, 1)}
                >
                  아래로
                </ActionButton>
                <ActionButton
                  type="button"
                  danger
                  onClick={() =>
                    onChange(
                      "staggeredStartTimes",
                      value.staggeredStartTimes.filter((_, current) => current !== index)
                    )
                  }
                >
                  삭제
                </ActionButton>
              </ActionRow>}
            </li>
          ))}
        </RuleList>
        {!readOnly && <ActionRow>
          <ActionButton
            type="button"
            onClick={() => onChange("staggeredStartTimes", [...value.staggeredStartTimes, "09:00"])}
          >
            허용 시간 추가
          </ActionButton>
        </ActionRow>}
        {errors.staggeredStartTimes && <ErrorText id="work-policy-staggeredStartTimes-error">{errors.staggeredStartTimes}</ErrorText>}
      </FormSection>
    );
  }

  const workMinutes = Math.round(Number(value.dailyBaseHours) * 60);
  const automaticBreak = calculateAutomaticBreakMinutes(
    workMinutes,
    value.breakCalculationEnabled,
    Number(value.breakWorkMinutes),
    Number(value.breakMinutes)
  );
  const expected = calculateExpectedEndTime(
    value.baseDefaultStartTime,
    workMinutes,
    automaticBreak
  );

  return (
    <FormSection>
      <legend>시차출퇴근제 추가 설정</legend>
      <p>기본 출근시간과 근로·휴게 기준으로 예상 퇴근시간을 확인합니다.</p>
      <FormGrid>
        <Field>
          고정 출근 시간
          <input
            id="work-policy-baseDefaultStartTime"
            type="time"
            value={value.baseDefaultStartTime}
            disabled={readOnly}
            aria-invalid={Boolean(errors.baseDefaultStartTime)}
            aria-describedby={errors.baseDefaultStartTime ? "work-policy-baseDefaultStartTime-error" : undefined}
            onChange={event => onChange("baseDefaultStartTime", event.currentTarget.value)}
          />
          {errors.baseDefaultStartTime && <ErrorText id="work-policy-baseDefaultStartTime-error">{errors.baseDefaultStartTime}</ErrorText>}
        </Field>
        <Field>
          계산된 퇴근 시간
          <input
            type="text"
            readOnly
            disabled={readOnly}
            aria-label="계산된 퇴근 시간"
            value={expected ? `${expected.crossesDay ? "다음 날 " : ""}${expected.time}` : "계산 불가"}
          />
          <small>
            출근 + 근로 + 자동 휴게 = {value.baseDefaultStartTime || "--:--"} + {workMinutes}분 + {automaticBreak}분
          </small>
        </Field>
      </FormGrid>
    </FormSection>
  );
};
